// @ts-check
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { isUrlValid } from 'utils';
import { NO_IMAGE_AVAILABLE } from 'constants/index';
import { Box } from '@material-ui/core';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

/**
 * @typedef {Object} ImageBoxProps
 * @property {string} src
 * @property {string} alt
 * @property {string} [fallback]
 * @property {'NM' | 'BG'} [userBrand]
 * @property {boolean} [withLazyLoad]
 * @property {React.ReactNode} [children]
 */

/** @param {ImageBoxProps & React.ImgHTMLAttributes | LazyLoadImage} props */
const ImageBox = ({ classes, src, alt, fallback, userBrand, withLazyLoad, children, ...rest }) => {
  const fallbackURL = NO_IMAGE_AVAILABLE[userBrand];
  const sanitizedURL = isUrlValid(src) ? src : fallbackURL;
  const onError = (e) => {
    e.target.src = fallback || fallbackURL;
  };

  const lazyLoadImagePlaceholderComponent = <img src={fallbackURL} style={{ height: '100%', width: '100%' }} alt="" />;

  return (
    <Box className={classes.lazyLoadImageContainer}>
      {withLazyLoad ? (
        <LazyLoadImage
          effect="blur"
          placeholderSrc={fallbackURL}
          placeholder={lazyLoadImagePlaceholderComponent}
          src={sanitizedURL}
          className={classes.image}
          alt={alt}
          onError={onError}
          data-testid="lazy-load-image"
          {...rest}
        />
      ) : (
        <img src={sanitizedURL} alt={alt} onError={onError} {...rest} />
      )}
      {children}
    </Box>
  );
};

ImageBox.propTypes = {
  classes: PropTypes.shape({}),
  src: PropTypes.string,
  alt: PropTypes.string,
  fallback: PropTypes.string,
  userBrand: PropTypes.oneOf(['NM', 'BG']),
  withLazyLoad: PropTypes.bool,
};

ImageBox.defaultProps = {
  classes: {},
  alt: '',
  userBrand: 'NM',
};

export default memo(ImageBox);
